import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@tempmee/react';
import { useDownloadCycleExport } from '../../../../domain/Awards';
import { triggerBlobDownload } from '../../../../utils/downloadBlob';
import { showErrorMessage } from '../../../../utils';

/** The two audiences every export is split by. */
const AUDIENCES = ['Offices', 'Pros'];

/** The two report kinds a cycle exports. */
const KINDS = ['applications', 'feedback'];

/**
 * One export control. Fetches the CSV as a blob (the request needs the auth
 * header the client attaches, so it can't be a plain link) and saves it to
 * disk. A complete or empty cycle still downloads — the server returns a
 * header-only file rather than a 404 — so the button carries no phase gate.
 */
export const ExportLink = ({ audience, kind, cycleId }) => {
  const download = useDownloadCycleExport({
    onSuccess: ({ blob, filename }) => triggerBlobDownload(blob, filename),
    onError: () =>
      showErrorMessage({ message: `Could not download ${kind} (${audience}).` }),
  });

  return (
    <Button
      variant="text"
      size="small"
      loading={download.isPending}
      onClick={() => download.mutate({ cycleId, kind, audience })}
      // The visible label is only the audience, which appears four times per
      // row; the accessible name restates which export it is.
      aria-label={`Download ${kind} (${audience})`}
    >
      {audience}
    </Button>
  );
};

ExportLink.propTypes = {
  /** Visible label — which side of the cycle the export covers. */
  audience: PropTypes.oneOf(AUDIENCES).isRequired,
  /** Which report to pull. */
  kind: PropTypes.oneOf(KINDS).isRequired,
  /** The cycle the export belongs to. */
  cycleId: PropTypes.string.isRequired,
};

/**
 * One cell's worth of exports: the same report for offices and for pros.
 *
 * Pairing them keeps the table at eight columns rather than ten, which is what
 * stops it scrolling horizontally at common widths.
 */
export const ExportLinkGroup = ({ kind, cycleId }) => (
  // nowrap so the pair stays on one line; -ml-2 cancels the first Button's own
  // padding so the cell aligns with its column.
  <span className="-ml-2 inline-flex flex-nowrap items-center">
    {AUDIENCES.map((audience, index) => (
      <React.Fragment key={audience}>
        {/* Without a separator "Offices Pros" reads as two stray words. */}
        {index > 0 ? (
          <span aria-hidden className="text-text-secondary">·</span>
        ) : null}
        <ExportLink audience={audience} kind={kind} cycleId={cycleId} />
      </React.Fragment>
    ))}
  </span>
);

ExportLinkGroup.propTypes = {
  kind: PropTypes.oneOf(KINDS).isRequired,
  cycleId: PropTypes.string.isRequired,
};
